/*
    DonatePage.jsx
    寄付ページ - Donation Page
*/

import React from "react";
import { useCms } from "../cms/CmsProvider";
import { motion } from "framer-motion";
import { FaBuilding } from "react-icons/fa";
import PaymentNiTuite from '../components/molecules/AboutPayment';
import coanaLogo from '../assets/img/sponsor-coana-tokyo.png';
import { logoStyle } from '../cms/logoAdjust';
import '../assets/styles/sponsors.css';

// 会社名の改行対策：「株式会社」などの法人格と社名本体をそれぞれ途中で改行しないまとまりにする
const ENTITY_RE = /(株式会社|有限会社|合同会社|合資会社|合名会社|一般社団法人|一般財団法人|公益社団法人|公益財団法人|特定非営利活動法人|NPO法人|社会福祉法人|医療法人|学校法人)/;
function CompanyName({ name }) {
    if (typeof name !== "string") return name ?? null;
    return name.split(ENTITY_RE).filter(Boolean).map((part, i) => (
        <span className="name-chunk" key={i}>{part}</span>
    ));
}

// 協賛企業カード：URLがあればリンクにする
function SponsorLink({ url, children }) {
    const ok = typeof url === "string" && /^https?:\/\//.test(url.trim());
    return ok
        ? <a className="sponsor-card sponsor-card-link" href={url.trim()} target="_blank" rel="noopener noreferrer">{children}</a>
        : <div className="sponsor-card">{children}</div>;
}

export default function DonatePage() {
    const { t, text, items, raw, isJa, cmsOn, site } = useCms("donate");
    // 法人・団体向け協賛フォーム（microCMS「サイト共通」の sponsorFormUrl で変更可）
    const sponsorFormUrl = (site && site.sponsorFormUrl) || "https://docs.google.com/forms/d/e/1FAIpQLSewENMC3ZYCMx2Hz_f9u-x9y8OAeHPafqU2PR1JPOs8WVgWMw/viewform";
    const corporateButton = (cmsOn && raw("corporateButton")) || (isJa ? "協賛申し込みフォームへ" : "Sponsorship application form");
    const supporters = cmsOn && typeof raw("supportersList") === "string" ? raw("supportersList").split(/\r?\n/).map((x) => x.trim()).filter(Boolean) : [];
    const tierOf = (c) => (Array.isArray(c.tier) ? c.tier[0] : c.tier) || "ゴールドスポンサー";
    // 新方式：microCMS「協賛企業一覧」（1社ごとにまとめて編集）。空なら旧方式（協賛1〜6）を使う
    const cmsList = cmsOn && Array.isArray(raw("sponsorList")) ? raw("sponsorList").filter((c) => c && c.name) : [];
    function allSponsors() {
        return cmsList.length > 0
            ? cmsList.map((c) => ({ title: c.name, logo: c.logo, adjust: c, tier: c.tier, url: c.url }))
            : sponsors;
    }
    function allSupporters() {
        const fromList = allSponsors().filter((c) => tierOf(c) === "サポーター").map((c) => c.title);
        return [...fromList, ...supporters.filter((n) => !fromList.includes(n))];
    }
    const defaultCompanies = t("donatePage.sponsors.gold.companies", { returnObjects: true });
    const sponsors = items("sponsors", (Array.isArray(defaultCompanies) ? defaultCompanies : []).map((c, i) => ({
        title: c.name,
        logo: i === 0 ? { url: coanaLogo } : null,
    }))).map((c) => ({
        ...c,
        // ロゴ未登録でも、従来から掲載している企業なら従来のロゴを使う
        logo: c.logo?.url ? c.logo : (Array.isArray(defaultCompanies) && c.title === defaultCompanies[0]?.name ? { url: coanaLogo } : null),
    }));

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.2, duration: 0.6 }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
    };

    return (
        <div className="donate-page">
            {/* Header */}
            <section className="donate-page-header">
                <motion.h1
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    {text("pageTitle", "donatePage.title")}
                </motion.h1>
            </section>

            {/* Payment Section */}
            <PaymentNiTuite />

            {/* Corporate Support Section */}
            <motion.section
                className="corporate-support-section"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, threshold: 0.3 }}
            >
                <motion.div className="corporate-support-card" variants={itemVariants}>
                    <h3><FaBuilding /> {text("corporateTitle", "donatePage.corporateSupport.title")}</h3>
                    <p>{text("corporateDescription", "donatePage.corporateSupport.description")}</p>
                    <a
                        href={sponsorFormUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="supporting-member-button"
                    >
                        {corporateButton}
                    </a>
                </motion.div>
            </motion.section>

            {/* Sponsors Section */}
            <motion.section
                className="sponsors-section"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, threshold: 0.3 }}
            >
                <motion.h2 variants={itemVariants}>{text("sponsorsTitle", "donatePage.sponsors.title")}</motion.h2>
                {[["ゴールドスポンサー","gold"],["シルバースポンサー","silver"],["ブロンズスポンサー","bronze"]].map(([tier, cls]) => {
                    const list = allSponsors().filter((c) => tierOf(c) === tier);
                    if (list.length === 0 && cls !== "gold") return null;
                    return (
                        <motion.div className={`sponsor-tier sponsor-tier-${cls}`} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: "easeOut" }} key={cls}>
                            <h3 className="sponsor-tier-title">{cls === "gold" ? text("sponsorTierTitle", "donatePage.sponsors.gold.title") : (isJa ? tier : { silver: "Silver Sponsors", bronze: "Bronze Sponsors" }[cls])}</h3>
                            <div className={`sponsor-logos sponsor-logos-${cls}`}>
                                {list.map((company, index) => (
                                    <SponsorLink url={company.url} key={index}>
                                        {cls !== "bronze" && (
                                            <div className="sponsor-logo-frame">
                                                {company.logo?.url && <img src={company.logo.url} alt={company.title} className="sponsor-logo" style={logoStyle(company.adjust)} />}
                                            </div>
                                        )}
                                        <p className="sponsor-name"><CompanyName name={company.title} /></p>
                                    </SponsorLink>
                                ))}
                            </div>
                        </motion.div>
                    );
                })}
                {allSupporters().length > 0 && (
                    <motion.div className="sponsor-tier sponsor-tier-supporter" variants={itemVariants}>
                        <h3 className="sponsor-tier-title">サポーター</h3>
                        <ul className="supporter-list">
                            {allSupporters().map((name) => <li key={name}><CompanyName name={name} /></li>)}
                        </ul>
                    </motion.div>
                )}
            </motion.section>
        </div>
    );
}
